/**
 * Correspondence Package Service Wrapper
 *
 * This file wraps the generated correspondence service to provide a cleaner API
 * and exports the RTK Query hooks for use in components.
 *
 * The generated service uses the enhancedApi pattern - this wrapper re-exports
 * the hooks with proper naming and adds any custom enhancements.
 */

import {
  CorrespondenceComponentSchema,
  CorrespondencePackageSchema as GeneratedCorrespondencePackageSchema,
  enhancedApi as generatedApi,
  PackageValidationResponse as GeneratedPackageValidationResponse,
  TemplateFormat,
  TemplateSearchResult as GeneratedTemplateSearchResult,
} from './generated/correspondenceService';
import type { MatterFlattenedContext } from './matterFlattenedContextService';

// Re-export types from the generated file
export type {
  BlobSchema,
  ChequeTransmission,
  ComponentOrderMapping,
  ComponentType,
  ComponentValidationResult,
  ContactSource,
  ContactSourceType,
  CorrespondenceComponentSchema,
  CorrespondenceComponentSearchTemplatesApiArg,
  CorrespondenceComponentValidateAllApiArg,
  CorrespondencePackageType,
  CreateCorrespondenceComponent,
  CreateCorrespondencePackage,
  CustomUserSchema,
  DestinationAddress,
  EmailContentDefaults,
  EmailTransmission,
  FaxTransmission,
  MailTransmission,
  MissingFieldInfo,
  PackageSource,
  PostGridMailingClass,
  ReadTemplate,
  ContactCardType as RecipientEntityType,
  RenderStatus,
  RenderWorkflowResponse,
  ReorderComponentsRequest,
  SendPackageRequest,
  SendPackageResponse,
  TemplateCategory,
  TemplateFormat,
  TemplateSearchResults,
  TemplateStatus,
  Transmission,
  TransmissionStatus,
  UpdateCorrespondenceComponent,
  UpdateCorrespondencePackage,
} from './generated/correspondenceService';

/**
 * Extended template search result with properly typed template_type field.
 * Uses Omit to override the generated string type with the TemplateType union.
 */
export type TemplateSearchResult = Omit<
  GeneratedTemplateSearchResult,
  'template_type'
> & {
  template_type?: TemplateFormat;
};

/**
 * Correspondence channel types - the subset of MessageType supported for correspondence
 */
export type CorrespondenceChannel =
  | 'fax'
  | 'email'
  | 'mailed_letter'
  | 'mailed_cheque';

/**
 * Enhanced CorrespondencePackageSchema with properly typed matter_context.
 * The package now includes flattened template context for frontend consumption,
 * eliminating the need for a separate matterContext query.
 */
export interface CorrespondencePackageSchema
  extends Omit<GeneratedCorrespondencePackageSchema, 'matter_context'> {
  matter_context?: MatterFlattenedContext;
}

/**
 * Enhanced PackageValidationResponse with properly typed matter_context.
 * The generated type has matter_context as a generic object; this provides
 * the canonical MatterFlattenedContext type for frontend consumption.
 */
export interface PackageValidationResponse
  extends Omit<GeneratedPackageValidationResponse, 'matter_context'> {
  matter_context: MatterFlattenedContext;
}

// Re-export API arg types for flexibility
export type {
  CorrespondenceComponentCreateApiArg,
  CorrespondenceComponentDestroyApiArg,
  CorrespondenceComponentListApiArg,
  CorrespondenceComponentReorderApiArg,
  CorrespondenceComponentRetrieveApiArg,
  CorrespondenceComponentUpdateApiArg,
  CorrespondencePackageCreateApiArg,
  CorrespondencePackageDefaultEmailContentApiArg,
  CorrespondencePackageDestroyApiArg,
  CorrespondencePackageGetOrCreateForCoverageApiArg,
  CorrespondencePackageListApiArg,
  CorrespondencePackageRenderApiArg,
  CorrespondencePackageRetrieveApiArg,
  CorrespondencePackageSendApiArg,
  CorrespondencePackageUpdateApiArg,
  CorrespondencePackageUploadApiArg,
} from './generated/correspondenceService';

// Enhance the generated API with proper tags
export const correspondenceApi = generatedApi.enhanceEndpoints({
  addTagTypes: [
    'correspondence-package',
    'correspondence-component',
    'correspondence-validation',
  ],
  endpoints: {
    correspondencePackageRetrieve: {
      providesTags: (result) =>
        result
          ? [{ type: 'correspondence-package' as const, id: result.id }]
          : [],
    },
    correspondencePackageList: {
      providesTags: (result) =>
        result
          ? [
              ...result.map((pkg: CorrespondencePackageSchema) => ({
                type: 'correspondence-package' as const,
                id: pkg.id,
              })),
              { type: 'correspondence-package' as const, id: 'LIST' },
            ]
          : [{ type: 'correspondence-package' as const, id: 'LIST' }],
    },
    correspondenceComponentList: {
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.map((component: CorrespondenceComponentSchema) => ({
                type: 'correspondence-component' as const,
                id: component.id,
              })),
              {
                type: 'correspondence-component' as const,
                id: `PACKAGE-${arg.packageId}`,
              },
            ]
          : [],
    },
    correspondencePackageCreate: {
      invalidatesTags: [
        { type: 'correspondence-package' as const, id: 'LIST' },
      ],
    },
    correspondencePackageGetOrCreateForCoverage: {
      invalidatesTags: [
        { type: 'correspondence-package' as const, id: 'LIST' },
      ],
    },
    correspondencePackageUpdate: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
        { type: 'correspondence-validation' as const, id: arg.id },
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.id}`,
        },
      ],
    },
    correspondencePackageDestroy: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
        { type: 'correspondence-package' as const, id: 'LIST' },
      ],
    },
    correspondencePackageRender: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
      ],
    },
    correspondencePackageSend: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
      ],
    },
    correspondenceComponentCreate: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.packageId}`,
        },
        { type: 'correspondence-package' as const, id: arg.packageId },
        { type: 'correspondence-validation' as const, id: arg.packageId },
      ],
    },
    correspondenceComponentUpdate: {
      // Don't invalidate correspondence-package - optimistic update sets status
      // to queued_for_render, let polling pick up the render completion
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-component' as const, id: arg.id },
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.packageId}`,
        },
        { type: 'correspondence-validation' as const, id: arg.packageId },
      ],
    },
    correspondenceComponentDestroy: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-component' as const, id: arg.id },
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.packageId}`,
        },
        { type: 'correspondence-package' as const, id: arg.packageId },
        { type: 'correspondence-validation' as const, id: arg.packageId },
      ],
    },
    correspondenceComponentReorder: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.packageId}`,
        },
        { type: 'correspondence-package' as const, id: arg.packageId },
      ],
    },
    correspondenceComponentValidateAll: {
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.packageId}`,
        },
        { type: 'correspondence-package' as const, id: arg.packageId },
      ],
    },
    correspondencePackageResetComponents: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
        { type: 'correspondence-component' as const, id: `PACKAGE-${arg.id}` },
        { type: 'correspondence-validation' as const, id: arg.id },
      ],
    },
    correspondencePackageTransmissionUpdate2: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'correspondence-package' as const, id: arg.id },
        { type: 'correspondence-validation' as const, id: arg.id },
        {
          type: 'correspondence-component' as const,
          id: `PACKAGE-${arg.id}`,
        },
      ],
    },
    correspondencePackageValidateAll: {
      providesTags: (_result, _error, arg) => [
        { type: 'correspondence-validation' as const, id: arg.id },
      ],
    },
  },
});

// Export hooks for use in components
export const {
  // Package queries
  useCorrespondencePackageListQuery,
  useCorrespondencePackageRetrieveQuery,
  useLazyCorrespondencePackageRetrieveQuery,
  useCorrespondencePackageDefaultEmailContentQuery,
  useLazyCorrespondencePackageDefaultEmailContentQuery,

  // Package mutations
  useCorrespondencePackageCreateMutation,
  useCorrespondencePackageGetOrCreateForCoverageMutation,
  useCorrespondencePackageUpdateMutation,
  useCorrespondencePackageDestroyMutation,
  useCorrespondencePackageRenderMutation,
  useCorrespondencePackageResetComponentsMutation,
  useCorrespondencePackageSendMutation,
  useCorrespondencePackageUploadMutation,
  useCorrespondencePackageTransmissionUpdate2Mutation,

  // Component queries
  useCorrespondenceComponentListQuery,
  useCorrespondenceComponentRetrieveQuery,
  useLazyCorrespondenceComponentListQuery,
  useCorrespondenceComponentSearchTemplatesQuery,

  // Component mutations
  useCorrespondenceComponentCreateMutation,
  useCorrespondenceComponentUpdateMutation,
  useCorrespondenceComponentDestroyMutation,
  useCorrespondenceComponentReorderMutation,
  useCorrespondenceComponentValidateAllMutation,

  // Validation query (provides matter_context via RTKQ cache)
  useCorrespondencePackageValidateAllQuery,
} = correspondenceApi;

// Export the API for store configuration
export default correspondenceApi;
